import { useState, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import toInteger from 'lodash/toInteger'
import Page from '@/components/page'
import { toUnit } from '@/utils/styles'
import { breakpoints, structure } from '@/styles'
import { showConnectPadSuccessModal, useConnectPadErrorModal } from '@/features/Pad'
import { AgreementForm, useAgreementForm } from './agreement-form'
import PadButtonBar from './pad-button-bar'
import { StyledContentWrapper, StyledAreaTitle } from './elements'
import Context from './context'

const StyledForm = styled(Form)`
  overflow-y: scroll;
  height: calc(100vh - ${toUnit(structure.header.height * 2)});
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - ${toUnit(structure.header.heightMobile)});
    height: unset;
    overflow-y: unset;
  }
`

const Review = ({ handleBack, onSuccess, handleDelete, padContract, isModal, padInfo }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [paymentData, setPaymentData] = useState(null)
  const { formData } = useContext(Context)
  const [signatures, setSignatures] = useState([])
  const { show: showErrorModal } = useConnectPadErrorModal()
  const listingId = padContract?.listing_id

  useEffect(() => {
    const loadPadPayment = async () => {
      const { body } = await dispatch.payment.getPadPayment({ listingId })
      setPaymentData(body)
    }
    if (listingId) loadPadPayment()
  }, [dispatch.payment, listingId])

  const { data = {}, isValid, current: contract, mutate } = useAgreementForm([padContract])
  const hasMore = data.count > data.index + 1
  const hasMulti = data.count > 1
  const initialValues = {
    ...formData
  }

  const onSubmit = async (values, { setSubmitting, setFieldValue }) => {
    setSubmitting(true)
    const addSignature = signatures => {
      const next = [
        {
          contract_id: toInteger(contract?.listing_contract_id),
          signature: data?.signature,
          payment_request_id: toInteger(paymentData?.id)
        }
      ]
      return signatures.concat(next)
    }
    let response = { ok: false }
    if (hasMore) {
      mutate(({ index, ...rest }) => ({
        index: index + 1,
        terms1: false,
        terms2: false,
        signature: null
      }))
      setSignatures(addSignature)
      setSubmitting(false)
      return
    }
    const responseData = await dispatch.settlements.updatePad({
      ...values,
      user_signatures: addSignature(signatures)
    })
    response = responseData

    setSubmitting(false)
    if (!response.ok) {
      return showErrorModal()
    }
    showConnectPadSuccessModal({ contracts: [padContract], paymentIds: [paymentData?.id] })
    onSuccess()
  }

  const buttonText = (() => {
    if (hasMore) return t('common.next', 'Next')
    if (!hasMulti)
      return t('l.payment_settings.view.authorize_payments.button', 'Authorize Payments')
    return t('l.payment_settings.view.authorize_all_payments.button', 'Authorize All Payments')
  })()

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {({ values, isSubmitting }) => {
        const previewValues = {
          ...values,
          account_number: values?.account_number || padInfo?.financial_info?.account_number,
          transit_number: values?.transit_number || padInfo?.financial_info?.transit_number
        }
        return (
          <StyledForm className="flex-full">
            <Page>
              <StyledContentWrapper isModal={isModal}>
                <StyledAreaTitle isModal={isModal}>
                  {t('l.payment_settings.view.review_pad.subtitle', 'Review PAD Agreement')}
                </StyledAreaTitle>
                <AgreementForm
                  contract={contract}
                  data={data}
                  mutate={mutate}
                  values={previewValues}
                  paymentData={paymentData}
                  isModal={isModal}
                />
              </StyledContentWrapper>
            </Page>
            <PadButtonBar
              isModal={isModal}
              handleBack={handleBack}
              handleDelete={handleDelete}
              disabled={!isValid || isSubmitting}
              primaryButtonText={buttonText}
            />
          </StyledForm>
        )
      }}
    </Formik>
  )
}

export default Review
